import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, ExternalLink, Clock, Heart, AlertCircle, AlertTriangle, Share2, X, Mail, CalendarPlus, Lock, Ticket, Bell, Star, Sparkles, Briefcase, GraduationCap, Zap } from "lucide-react";
import { useFavoriteEvents } from "@/hooks/useFavoriteEvents";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

// Custom icons for social platforms
const TelegramIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>;
const WhatsAppIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>;
const XIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>;
interface ImportedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  ticket_status: string | null;
  created_at?: string;
}
interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}
interface EventCard3DProps {
  event: ImportedEvent;
  isRTL: boolean;
  countdown: CountdownTime | null;
}
export const EventCard3D = ({
  event,
  isRTL,
  countdown
}: EventCard3DProps) => {
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const {
    toggleFavorite,
    isFavorite
  } = useFavoriteEvents();
  const { playHoverSound, playClickSound, playFavoriteSound, playShareSound } = useInteractionSounds();
  const { user } = useAuth();
  const navigate = useNavigate();

  const registrationBenefits = [
    {
      icon: Ticket,
      title: isRTL ? "حجز التذاكر" : "Book Tickets",
      description: isRTL ? "احجز تذاكرك مباشرة من المنصة" : "Book your tickets directly from the platform"
    },
    {
      icon: Heart,
      title: isRTL ? "حفظ المفضلة" : "Save Favorites",
      description: isRTL ? "احفظ الفعاليات المفضلة لديك" : "Save your favorite events"
    },
    {
      icon: Bell,
      title: isRTL ? "التذكيرات" : "Reminders",
      description: isRTL ? "احصل على تنبيهات قبل الفعاليات" : "Get notified before events"
    },
    {
      icon: Star,
      title: isRTL ? "توصيات مخصصة" : "Personalized Picks",
      description: isRTL ? "فعاليات مقترحة بناءً على اهتماماتك" : "Events suggested based on your interests"
    }
  ];
  
  const eventTitle = isRTL ? event.title_ar || event.title : event.title;
  const eventDescription = isRTL ? event.description_ar || event.description : event.description;
  const eventLocation = isRTL ? event.location_ar || event.location : event.location;
  // Always use platform URL for sharing, not external link
  const platformBaseUrl = "https://visitevent.live";
  const shareUrl = `${platformBaseUrl}/event/${event.id}`;
  const shareText = `${eventTitle}${event.date ? ` - ${event.date}` : ''}`;
  const shareLinks = [{
    name: "X",
    icon: XIcon,
    url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    bg: "bg-black hover:bg-gray-800"
  }, {
    name: "Telegram",
    icon: TelegramIcon,
    url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    bg: "bg-[#0088cc] hover:bg-[#0077b5]"
  }, {
    name: "WhatsApp",
    icon: WhatsAppIcon,
    url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    bg: "bg-[#25D366] hover:bg-[#20bd5a]"
  }, {
    name: isRTL ? "البريد" : "Email",
    icon: Mail,
    url: `mailto:?subject=${encodeURIComponent(eventTitle)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
    bg: "bg-gray-600 hover:bg-gray-700"
  }, {
    name: isRTL ? "التقويم" : "Calendar",
    icon: CalendarPlus,
    url: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${event.date ? event.date.replace(/-/g, '') : ''}&details=${encodeURIComponent(eventDescription || '')}&location=${encodeURIComponent(eventLocation || '')}`,
    bg: "bg-[#4285f4] hover:bg-[#3367d6]"
  }];

  const triggerConfetti = useCallback(() => {
    if (!favoriteButtonRef.current) return;
    
    const rect = favoriteButtonRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    // Heart-themed confetti burst
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x, y },
      colors: ['#ff6b6b', '#ee5a5a', '#ff8787', '#ffa8a8', '#ffd1d1'],
      shapes: ['circle'],
      scalar: 0.8,
      gravity: 0.8,
      drift: 0,
      ticks: 150
    });

    // Second burst with hearts emoji effect
    setTimeout(() => {
      confetti({
        particleCount: 25,
        spread: 40,
        origin: { x, y },
        colors: ['#ff4757', '#ff6b81', '#ff7f7f'],
        shapes: ['circle'],
        scalar: 1.2,
        gravity: 1,
        ticks: 100
      });
    }, 100);
  }, []);

  const handleFavoriteClick = useCallback(() => {
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }
    const wasAlreadyFavorite = isFavorite(event.id);
    playFavoriteSound(!wasAlreadyFavorite);
    
    // Trigger confetti only when ADDING to favorites
    if (!wasAlreadyFavorite) {
      triggerConfetti();
    }
    
    toggleFavorite(event.id, isRTL);
  }, [event.id, isRTL, isFavorite, playFavoriteSound, toggleFavorite, triggerConfetti, user]);

  const handleLoginPromptNavigate = useCallback(() => {
    setIsLoginPromptOpen(false);
    navigate("/auth");
  }, [navigate]);

  const handleShareClick = useCallback(() => {
    playShareSound(!isShareOpen);
    setIsShareOpen(!isShareOpen);
  }, [isShareOpen, playShareSound]);

  const handleCardHover = useCallback(() => {
    playHoverSound();
  }, [playHoverSound]);

  const handleBookClick = useCallback(() => {
    playClickSound();
  }, [playClickSound]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    playClickSound();
    navigate(`/event/${event.id}`);
  }, [event.id, navigate, playClickSound]);

  return (
    <>
      {/* Login Prompt Dialog */}
      <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 rounded-3xl" style={{
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          boxShadow: '20px 20px 60px #d1d1d1, -20px -20px 60px #ffffff'
        }}>
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-primary via-primary/90 to-emerald-600 p-6 text-white text-center">
            <div className="absolute inset-0 bg-black/10" />
            <motion.div 
              initial={{ scale: 0 }} 
              animate={{ scale: 1 }} 
              transition={{ type: "spring", delay: 0.1 }}
              className="relative"
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-1">
                {isRTL ? "انضم إلينا!" : "Join Us!"}
              </h3>
              <p className="text-white/80 text-sm">
                {isRTL ? "سجل للاستمتاع بكل المميزات" : "Register to enjoy all features"}
              </p>
            </motion.div>
          </div>

          {/* Benefits List */}
          <div className="p-5 space-y-3">
            {registrationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-4 p-3 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
                  boxShadow: '3px 3px 8px #e8e8e8, -3px -3px 8px #ffffff'
                }}
              >
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, hsl(var(--primary) / 0.15), hsl(var(--primary) / 0.05))'
                  }}
                >
                  <benefit.icon className="w-5 h-5 text-primary" />
                </div>
                <div className={isRTL ? "text-right" : "text-left"}>
                  <h4 className="font-semibold text-gray-700 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-500">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="p-5 pt-2 space-y-3">
            <Button 
              onClick={handleLoginPromptNavigate}
              className="w-full py-6 rounded-2xl text-base font-semibold bg-gradient-to-r from-primary to-primary-400 hover:from-primary/90 hover:to-primary-400/90 transition-all"
              style={{
                boxShadow: '5px 5px 15px #c0c0c0, -5px -5px 15px #ffffff'
              }}
            >
              {isRTL ? "سجل الآن مجاناً" : "Register Now - Free"}
            </Button>
            <button 
              onClick={() => setIsLoginPromptOpen(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isRTL ? "لاحقاً" : "Maybe Later"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <motion.div 
        className="relative group h-full cursor-pointer" 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.4 }} 
        whileHover={{ y: -5 }} 
        onHoverStart={handleCardHover} 
        onClick={handleCardClick}
      >
      {/* Neumorphism Card */}
      <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 h-full flex flex-col border border-primary/10" style={{
      background: 'linear-gradient(145deg, hsl(30 15% 97%), hsl(30 12% 99%))',
      boxShadow: '6px 6px 15px hsl(278 20% 85% / 0.4), -6px -6px 15px hsl(0 0% 100%)'
    }}>
        {/* Inner Shadow Overlay for pressed effect on hover */}
        <div className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
        boxShadow: 'inset 4px 4px 10px hsl(278 20% 85% / 0.3), inset -4px -4px 10px hsl(0 0% 100%)'
      }} />

        {/* Favorite Button - Neumorphic */}
        <button ref={favoriteButtonRef} onClick={handleFavoriteClick} className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-105" style={{
        background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
        boxShadow: '3px 3px 8px #d1d1d1, -3px -3px 8px #ffffff'
      }}>
          <Heart className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${isFavorite(event.id) ? "fill-red-500 text-red-500" : "text-gray-400"}`} />
        </button>

        {/* Share Button - Neumorphic */}
        <button onClick={handleShareClick} className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-105" style={{
        background: isShareOpen ? 'linear-gradient(145deg, #e6e6e6, #ffffff)' : 'linear-gradient(145deg, #ffffff, #e6e6e6)',
        boxShadow: isShareOpen ? 'inset 2px 2px 5px #d1d1d1, inset -2px -2px 5px #ffffff' : '3px 3px 8px #d1d1d1, -3px -3px 8px #ffffff'
      }}>
          <Share2 className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${isShareOpen ? 'text-primary' : 'text-gray-400'}`} />
        </button>

        {/* Share Menu Popup - Neumorphic */}
        <AnimatePresence>
          {isShareOpen && <motion.div initial={{
          opacity: 0,
          scale: 0.8,
          y: -10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.8,
          y: -10
        }} className="absolute top-16 left-4 z-30 rounded-2xl p-4 min-w-[200px]" style={{
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          boxShadow: '10px 10px 30px #d1d1d1, -10px -10px 30px #ffffff'
        }}>
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200/50">
                <span className="text-sm font-semibold text-gray-600">
                  {isRTL ? "مشاركة" : "Share"}
                </span>
                <button onClick={() => setIsShareOpen(false)} className="p-1.5 rounded-full transition-all" style={{
              background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
              boxShadow: '2px 2px 5px #d1d1d1, -2px -2px 5px #ffffff'
            }}>
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {shareLinks.map(link => <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-center w-10 h-10 rounded-xl text-white transition-all hover:scale-110 ${link.bg}`} style={{
              boxShadow: '3px 3px 8px rgba(0,0,0,0.2)'
            }} title={link.name}>
                    <link.icon />
                  </a>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Image with Soft Overlay */}
        {event.image_url && <div className="relative h-36 sm:h-44 md:h-52 overflow-hidden m-2 sm:m-3 rounded-xl sm:rounded-2xl">
            <img 
              src={event.image_url} 
              alt={eventTitle} 
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }} 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-xl sm:rounded-2xl" />
            
            {/* New Job Badge - for jobs added in last 24 hours */}
            {event.category === 'Jobs' && event.created_at && 
              (new Date().getTime() - new Date(event.created_at).getTime()) < 24 * 60 * 60 * 1000 && (
              <div className="absolute top-3 left-12">
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 px-2.5 py-1 rounded-lg shadow-lg animate-pulse">
                  <Zap className="w-3 h-3 mr-1" />
                  {isRTL ? "جديد" : "New"}
                </Badge>
              </div>
            )}
            
            {/* Ticket Status Badge - Neumorphic */}
            {event.ticket_status && event.ticket_status !== 'available' && <div className="absolute bottom-3 right-3">
                {event.ticket_status === 'sold_out' ? <Badge className="bg-red-500/90 text-white border-0 px-3 py-1.5 rounded-xl shadow-lg">
                    <AlertCircle className="w-3.5 h-3.5 mr-1" />
                    {isRTL ? "نفذت التذاكر" : "Sold Out"}
                  </Badge> : event.ticket_status === 'limited' && <Badge className="bg-orange-500/90 text-white border-0 px-3 py-1.5 rounded-xl shadow-lg">
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                    {isRTL ? "تذاكر محدودة" : "Limited"}
                  </Badge>}
              </div>}
          </div>}

        {/* Content */}
        <div className="p-3 sm:p-5 pt-1 sm:pt-2 flex flex-col flex-grow">
          {/* Source Badge - Neumorphic */}
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg" style={{
            background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
            boxShadow: '2px 2px 5px #e0e0e0, -2px -2px 5px #ffffff'
          }}>
              {event.source_name}
            </span>
          </div>
          
          {/* Title */}
          <h3 className="text-sm sm:text-base md:text-lg font-bold mb-1 sm:mb-2 text-gray-700 line-clamp-2 group-hover:text-primary transition-colors">
            {eventTitle}
          </h3>
          
          {/* Description */}
          {eventDescription && <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2 hidden sm:block">
              {eventDescription}
            </p>}
          
          {/* Countdown Timer - Neumorphic - Hidden on mobile */}
          {countdown && !countdown.isPast && <div className="rounded-xl sm:rounded-2xl p-2 sm:p-4 mb-2 sm:mb-4 hidden sm:block" style={{
          background: 'linear-gradient(145deg, #f5f5f5, #ffffff)',
          boxShadow: 'inset 3px 3px 8px #e0e0e0, inset -3px -3px 8px #ffffff'
        }}>
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-[10px] sm:text-xs font-semibold text-gray-500">
                  {isRTL ? "العد التنازلي" : "Countdown"}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1 sm:gap-2 text-center">
                {[{
              value: countdown.days,
              label: isRTL ? "يوم" : "Days"
            }, {
              value: countdown.hours,
              label: isRTL ? "ساعة" : "Hrs"
            }, {
              value: countdown.minutes,
              label: isRTL ? "دقيقة" : "Min"
            }, {
              value: countdown.seconds,
              label: isRTL ? "ثانية" : "Sec"
            }].map((item, idx) => <div key={idx} className="rounded-xl p-2" style={{
              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
              boxShadow: '3px 3px 8px #e0e0e0, -3px -3px 8px #ffffff'
            }}>
                    <div className="text-lg font-bold text-primary">
                      {item.value}
                    </div>
                    <div className="text-[10px] text-gray-400 font-medium">{item.label}</div>
                  </div>)}
              </div>
            </div>}
          
          {/* Spacer to push content down */}
          <div className="flex-grow" />
          
          {/* Event Details - Neumorphic icons */}
          <div className="space-y-3 text-sm text-gray-500 mb-5 mt-auto">
            {event.date && <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
              boxShadow: '3px 3px 6px #e0e0e0, -3px -3px 6px #ffffff'
            }}>
                  <Calendar className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-gray-600">{event.date}</span>
              </div>}
            {eventLocation && <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
              boxShadow: '3px 3px 6px #e0e0e0, -3px -3px 6px #ffffff'
            }}>
                  <MapPin className="w-4 h-4 text-emerald-500" />
                </div>
                <span className="font-medium text-gray-600 line-clamp-1">{eventLocation}</span>
              </div>}
          </div>
          
          {/* Book Now Button - Neumorphic */}
          {event.external_link && event.ticket_status !== 'sold_out' && (
            user ? (
              <motion.a 
                href={event.external_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={handleBookClick} 
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--primary)), hsl(150, 100%, 30%))',
                  boxShadow: '4px 4px 12px #c0c0c0, -4px -4px 12px #ffffff'
                }} 
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }} 
                className="flex items-center justify-center gap-2 w-full text-white px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base transition-all bg-ring"
              >
                {event.category === 'Jobs' 
                  ? <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  : event.category === 'Courses'
                    ? <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    : <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                {event.category === 'Jobs' 
                  ? (isRTL ? "قدم على الوظيفة" : "Apply for Job")
                  : event.category === 'Courses'
                    ? (isRTL ? "سجل في الدورة" : "Register for Course")
                    : (isRTL ? "احجز الآن" : "Book Now")}
              </motion.a>
            ) : (
              <motion.button 
                onClick={() => setIsLoginPromptOpen(true)}
                style={{
                  background: 'linear-gradient(145deg, #f5f5f5, #e0e0e0)',
                  boxShadow: '4px 4px 12px #c0c0c0, -4px -4px 12px #ffffff'
                }} 
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }} 
                className="flex items-center justify-center gap-2 w-full text-gray-600 px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base transition-all"
              >
                {event.category === 'Jobs' 
                  ? <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  : event.category === 'Courses'
                    ? <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    : <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                {event.category === 'Jobs'
                  ? (isRTL ? "سجل للتقديم" : "Login to Apply")
                  : event.category === 'Courses'
                    ? (isRTL ? "سجل للالتحاق" : "Login to Register")
                    : (isRTL ? "سجل دخولك للحجز" : "Login to Book")}
              </motion.button>
            )
          )}
        </div>
      </div>
    </motion.div>
    </>
  );
};